import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { initializeSocket } from "../utils/socketUtils";
import { getUserInfo } from "../../../api/usercall";
import { useUnreadCount } from "../../../contexts/UnreadCountContext";

import { ChatsUI } from "../comui/ChatsUI"; 

const Chats = ({ hideBottomBar, setActiveTab }) => {
  const location = useLocation();
  const otherUserIdParam = location.state?.user_id;
  const announcementLinkParam = location.state?.announcement_link;
  const announcementIdParam = location.state?.announcement_id;

  const [currentUserId, setCurrentUserId] = useState(null);
  const [chatList, setChatList] = useState([]);
  const [filteredChatList, setFilteredChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pinnedChats, setPinnedChats] = useState(() => {
    const saved = localStorage.getItem('pinnedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [chatOrder, setChatOrder] = useState(() => {
    const saved = localStorage.getItem('chatOrder');
    return saved ? JSON.parse(saved) : {};
  });
  const socketInstance = useRef(null);
  const currentUserIdRef = useRef(null);
  const { setTotalUnreadCount } = useUnreadCount();

  useEffect(() => {
    const fetchAndConnect = async () => {
      try {
        const userInfo = await getUserInfo();
        const userId = userInfo.user.id;
        setCurrentUserId(userId);
        currentUserIdRef.current = userId;
      } catch (error) {
        console.error("Error fetching user info:", error);
        return;
      }

      socketInstance.current = initializeSocket();

      const updateUnreadCount = (chats) => {
        if (chats && Array.isArray(chats)) {
          const total = chats.reduce((sum, chat) => sum + (chat.unread_count || 0), 0);
          setTotalUnreadCount(total);
          localStorage.setItem('totalUnreadCount', total.toString());
        }
      };

      socketInstance.current.on("connect_chat_success", (data) => {
        if (data?.chats) {
          setChatList(data.chats);
          updateUnreadCount(data.chats);
        }
      });

      socketInstance.current.on("send_updated_chat_list", (data) => {
        if (data?.chats) {
          setChatList(data.chats);
          updateUnreadCount(data.chats);
        }
      });

      const handleChatMessage = (data) => {
        const userId = currentUserIdRef.current;

        setChatList(prevChatList =>
          prevChatList.map(chat => {
            const isThisChat =
              (chat.sender_id === data.sender_id && chat.receiver_id === data.receiver_id) ||
              (chat.sender_id === data.receiver_id && chat.receiver_id === data.sender_id);

            if (isThisChat) {
              return {
                ...chat,
                message: data.message,
                timestamp: data.timestamp,
                is_read: data.sender_id === userId
              };
            }
            return chat;
          })
        );

        setSelectedChat(prevSelectedChat => {
          if (prevSelectedChat &&
            ((prevSelectedChat.sender_id === data.sender_id && prevSelectedChat.receiver_id === data.receiver_id) ||
             (prevSelectedChat.sender_id === data.receiver_id && prevSelectedChat.receiver_id === data.sender_id))) {
            return {
              ...prevSelectedChat,
              message: data.message,
              timestamp: data.timestamp,
              messages: [...(prevSelectedChat.messages || []), data]
            };
          }
          return prevSelectedChat;
        });
      };

      socketInstance.current.on("chat_message", handleChatMessage);

      socketInstance.current.on("join_chat_success", (data) => {
        setSelectedChat(prevChat => ({
          ...prevChat,
          room_name: data.room_name,
          messages: data.messages
        }));
      });

      socketInstance.current.on("connect_error", console.error);
      socketInstance.current.on("join_chat_error", console.error);
    };

    fetchAndConnect();

    return () => {
      if (socketInstance.current) {
        socketInstance.current.off("connect_chat_success");
        socketInstance.current.off("send_updated_chat_list");
        socketInstance.current.off("chat_message");
        socketInstance.current.off("join_chat_success");
        socketInstance.current.off("connect_error");
        socketInstance.current.off("join_chat_error");
        socketInstance.current.disconnect();
      }
    };
  }, [setTotalUnreadCount]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const isSpecialService = useCallback((chat) => {
    const specialServiceIds = [31, 1001, 1002, 1003, 1004];
    return specialServiceIds.includes(chat.sender_id) || specialServiceIds.includes(chat.receiver_id);
  }, []);

  useEffect(() => {
    if (currentUserId !== null && chatList.length > 0) {
      const filtered = chatList.filter(chat => {
        const otherParticipantName = chat.sender_id === currentUserId ? chat.receiver_name : chat.sender_name;
        return (otherParticipantName?.toLowerCase() || "").includes(searchQuery.toLowerCase());
      });

      const sortedFiltered = filtered.sort((a, b) => {
        const aIsPinned = pinnedChats.includes(a.room_id);
        const bIsPinned = pinnedChats.includes(b.room_id);
        const aIsSpecial = isSpecialService(a);
        const bIsSpecial = isSpecialService(b);

        if (aIsPinned && !bIsPinned) return -1;
        if (!aIsPinned && bIsPinned) return 1;

        if (aIsPinned && bIsPinned) {
          const aOrder = chatOrder[a.room_id] ?? pinnedChats.indexOf(a.room_id);
          const bOrder = chatOrder[b.room_id] ?? pinnedChats.indexOf(b.room_id);
          return aOrder - bOrder;
        }

        if (aIsSpecial && !bIsSpecial) return -1;
        if (!aIsSpecial && bIsSpecial) return 1;

        if (chatOrder[a.room_id] !== undefined && chatOrder[b.room_id] !== undefined) {
          return chatOrder[a.room_id] - chatOrder[b.room_id];
        }

        return 0;
      });

      setFilteredChatList(sortedFiltered);
    } else {
      setFilteredChatList([]);
    }
  }, [chatList, currentUserId, searchQuery, selectedChat, isSpecialService, pinnedChats, chatOrder]);

  useEffect(() => {
    if (otherUserIdParam && currentUserId !== null && chatList.length > 0) {
      const chatWithOtherUser = chatList.find(
        chat =>
          (chat.userId1 === currentUserId && chat.userId2 === otherUserIdParam) ||
          (chat.userId1 === otherUserIdParam && chat.userId2 === currentUserId)
      );

      if (chatWithOtherUser) {
        setSelectedChat(chatWithOtherUser);
        socketInstance.current.emit("join_chat", { other_user_id: otherUserIdParam });
      } else {
        socketInstance.current.emit("join_chat", {
          other_user_id: otherUserIdParam,
          announcement_link: announcementLinkParam || "",
          announcement_id: announcementIdParam || null
        });

        setSelectedChat({
          userId1: Math.min(currentUserId, otherUserIdParam),
          userId2: Math.max(currentUserId, otherUserIdParam),
          room_id: `chat_${Math.min(currentUserId, otherUserIdParam)}_${Math.max(currentUserId, otherUserIdParam)}`,
          announcement_link: announcementLinkParam || "",
          announcement_id: announcementIdParam || null,
          messages: []
        });
      }

      if (window.innerWidth <= 768) {
        setIsModalOpen(true);
        hideBottomBar?.(true);
      }
    }
  }, [otherUserIdParam, announcementLinkParam, announcementIdParam, chatList, currentUserId, hideBottomBar]);

  const isReadOnlyChat = useCallback((chat) => {
    const readOnlyServiceIds = [1001, 1002, 1004];
    return readOnlyServiceIds.includes(chat.sender_id) || readOnlyServiceIds.includes(chat.receiver_id);
  }, []);

  const handleUserClick = useCallback((chat) => {
    setSelectedChat(chat);
    const otherUserId = chat.sender_id === currentUserId ? chat.receiver_id : chat.sender_id;
    socketInstance.current.emit("join_chat", { other_user_id: otherUserId });

    if (window.innerWidth <= 768) {
      setIsModalOpen(true);
      hideBottomBar?.(true);
    }
  }, [currentUserId, hideBottomBar]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedChat(null);
    hideBottomBar?.(false);
  }, [hideBottomBar]);

  const handlePinChat = useCallback((chatId) => {
    setPinnedChats(prev => {
      const isPinned = prev.includes(chatId);
      const newPinned = isPinned ? prev.filter(id => id !== chatId) : [...prev, chatId];
      localStorage.setItem('pinnedChats', JSON.stringify(newPinned));
      return newPinned;
    });
  }, []);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const newOrder = { ...chatOrder };
    const reorderedList = Array.from(filteredChatList);
    const [removed] = reorderedList.splice(sourceIndex, 1);
    reorderedList.splice(destinationIndex, 0, removed);

    reorderedList.forEach((chat, index) => {
      newOrder[chat.room_id] = index;
    });

    setChatOrder(newOrder);
    localStorage.setItem('chatOrder', JSON.stringify(newOrder));
  }, [filteredChatList, chatOrder]);

  return ChatsUI(
    searchQuery,
    setSearchQuery,
    chatList,
    currentUserId,
    filteredChatList,
    handleUserClick,
    selectedChat,
    isModalOpen,
    socketInstance,
    closeModal,
    isReadOnlyChat,
    pinnedChats,
    handlePinChat,
    handleDragEnd,
    setActiveTab
  );
};

export default Chats;